/**
 * CreateHospitalModal — migrated to unified modal system.
 * 12 fields: name, code, city, address, department, contactName, contactPhone,
 * contactEmail, latitude, longitude, equipmentNotes, notes.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2Icon,
  HashIcon,
  MapPinIcon,
  PhoneIcon,
  MailIcon,
  UserIcon,
  WrenchIcon,
  StickyNoteIcon,
  GlobeIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import { useCreateHospital } from '../hooks';
import type { HospitalFormData } from '../types';

interface CreateHospitalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateHospitalModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateHospitalModalProps) {
  const { t } = useTranslation();
  const createHospital = useCreateHospital();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: 'HOSPITAL',
  });

  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [code, setCode] = useState('');
  const [city, setCity] = useState('');
  const [cityEn, setCityEn] = useState('');
  const [address, setAddress] = useState('');
  const [department, setDepartment] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [equipmentNotes, setEquipmentNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setName('');
      setNameEn('');
      setCode('');
      setCity('');
      setCityEn('');
      setAddress('');
      setDepartment('');
      setContactName('');
      setContactPhone('');
      setContactEmail('');
      setLatitude('');
      setLongitude('');
      setEquipmentNotes('');
      setNotes('');
      setError('');
    }
  }, [isOpen]);

  const focusField = (id: string) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
    });
  };

  const validateForm = (): boolean => {
    setError('');
    if (!name.trim()) {
      setError(t('validation.nameRequired'));
      focusField('ch-name');
      return false;
    }
    if (!code.trim()) {
      setError(t('validation.codeRequired'));
      focusField('ch-code');
      return false;
    }
    if (!city.trim()) {
      setError(t('validation.cityRequired'));
      focusField('ch-city');
      return false;
    }
    if (contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      setError(t('validation.invalidEmail'));
      focusField('ch-contactEmail');
      return false;
    }
    if (latitude.trim()) {
      const lat = parseFloat(latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setError(t('validation.invalidLatitude'));
        focusField('ch-latitude');
        return false;
      }
    }
    if (longitude.trim()) {
      const lng = parseFloat(longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setError(t('validation.invalidLongitude'));
        focusField('ch-longitude');
        return false;
      }
    }
    return true;
  };

  const isFormValid = useMemo(() => {
    if (!name.trim() || !code.trim() || !city.trim()) return false;
    if (contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) return false;
    return true;
  }, [name, code, city, contactEmail]);

  const handleSubmit = () => {
    const payload: HospitalFormData = {
      name: name.trim(),
      nameEn: nameEn.trim() || undefined,
      code: code.trim(),
      city: city.trim(),
      cityEn: cityEn.trim() || undefined,
      address: address.trim() || undefined,
      department: department.trim() || undefined,
      contactName: contactName.trim() || undefined,
      contactPhone: contactPhone.trim() || undefined,
      contactEmail: contactEmail.trim() || undefined,
      latitude: latitude.trim() ? parseFloat(latitude) : undefined,
      longitude: longitude.trim() ? parseFloat(longitude) : undefined,
      equipmentNotes: equipmentNotes.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () => createHospital.mutateAsync(payload),
      onSuccess: () => {
        toast.success(t('hospitals.created'));
        onSuccess();
        onClose();
      },
      onError: (err: any) => {
        setError(err.response?.data?.error || err.response?.data?.message || t('common.error'));
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '80px',
    resize: 'vertical' as const,
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('hospitals.createHospital')}
      isPending={createHospital.isPending}
      isAnimating={isAnimating}
      onSubmit={handleSubmit}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel="HOSPITAL"
      error={error}
    >
      <div className="space-y-3">
        {/* Name + Code */}
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<Building2Icon className="h-4 w-4" />} label={t('hospitals.name')} required>
            <input
              id="ch-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<HashIcon className="h-4 w-4" />} label={t('hospitals.code')} required>
            <input
              id="ch-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>

        {/* Name (English) + City (English) */}
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<GlobeIcon className="h-4 w-4" />} label={t('hospitals.nameEn')}>
            <input
              id="ch-nameEn"
              type="text"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<GlobeIcon className="h-4 w-4" />} label={t('hospitals.cityEn')}>
            <input
              id="ch-cityEn"
              type="text"
              value={cityEn}
              onChange={(e) => setCityEn(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>

        {/* City + Address */}
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<MapPinIcon className="h-4 w-4" />} label={t('hospitals.city')} required>
            <input
              id="ch-city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<MapPinIcon className="h-4 w-4" />} label={t('hospitals.address')}>
            <input
              id="ch-address"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>

        {/* Department */}
        <FormField icon={<Building2Icon className="h-4 w-4" />} label={t('hospitals.department')}>
          <input
            id="ch-department"
            type="text"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        {/* Contact Name + Contact Phone */}
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<UserIcon className="h-4 w-4" />} label={t('hospitals.contactName')}>
            <input
              id="ch-contactName"
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<PhoneIcon className="h-4 w-4" />} label={t('hospitals.contactPhone')}>
            <input
              id="ch-contactPhone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>

        {/* Contact Email */}
        <FormField icon={<MailIcon className="h-4 w-4" />} label={t('hospitals.contactEmail')}>
          <input
            id="ch-contactEmail"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        {/* Latitude + Longitude */}
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<GlobeIcon className="h-4 w-4" />} label={t('hospitals.latitude')}>
            <input
              id="ch-latitude"
              type="number"
              step="any"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<GlobeIcon className="h-4 w-4" />} label={t('hospitals.longitude')}>
            <input
              id="ch-longitude"
              type="number"
              step="any"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>

        {/* Equipment Notes */}
        <FormField icon={<WrenchIcon className="h-4 w-4" />} label={t('hospitals.equipmentNotes')}>
          <textarea
            id="ch-equipmentNotes"
            value={equipmentNotes}
            onChange={(e) => setEquipmentNotes(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={textareaStyle}
          />
        </FormField>

        {/* Notes */}
        <FormField icon={<StickyNoteIcon className="h-4 w-4" />} label={t('reports.notes')}>
          <textarea
            id="ch-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={textareaStyle}
          />
        </FormField>
      </div>
    </FormModal>
  );
}
